import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { get, isNil, reduce } from 'lodash';
import { rx, Observable, observableThrowError } from '@shared/rx';
import { IFRAME_MODE } from '@app/routes';
import { environment } from '../../../../environments/environment';
import {
  IFilterQuery,
  IUserSetting,
  IUserConfig,
  USER_CONFIG_MODULE_NAME,
  USER_CONFIG_MODULE_TYPE,
} from '@shared/types';
import { MAX_PAGE_SIZE } from '@config/global-constants';
import { createParams } from '@shared/common';

interface ISSRItem {
  name: string;
  description: string;
  key: string;
  url: string;
}

@Injectable()
export class ToolsService {
  public constructor(public http: HttpClient) {}

  public getEnvironmentInfo(): Observable<object> {
    return this.http.get(`/health`).pipe(this.catchHttpErrorAndPickErrorList());
  }

  /**
   * Defines whether application loaded externally
   */
  public isExternalLogin(): boolean {
    return (location.hash || '').startsWith(`#/${IFRAME_MODE}`);
  }

  /**
   * Method for getting component component list from Simple Service Registry
   *
   * @returns - array of component objects
   */
  public getComponentList(): Observable<Object> {
    const fullSsrURI = `${environment.ssrURI}?env=${environment.ssrEnv || environment.stage}`;

    return this.http.get<ISSRItem[]>(`${fullSsrURI}`).pipe(
      this.catchHttpErrorAndPickErrorList(),
      rx.map((response) =>
        reduce(
          response,
          (acc, item) => {
            acc[item.key] = item.url;

            return acc;
          },
          {},
        ),
      ),
    );
  }

  public getUserConfig(query?: IFilterQuery): Observable<object> {
    const defaultSelect = [
      'user_id',
      'module_type',
      'module_name',
      'module_config',
    ];
    const params: HttpParams = createParams({
      top: MAX_PAGE_SIZE,
      select: defaultSelect,
      ...query,
    });

    return this.http
      .get('/customization', { params })
      .pipe(rx.map((columnView) => (isNil(columnView) ? [] : columnView)));
  }

  public saveUserConfig(
    moduleType: USER_CONFIG_MODULE_TYPE,
    moduleName: USER_CONFIG_MODULE_NAME,
    moduleConfig: IUserSetting,
  ): Observable<object> {
    return this.http
      .patch('/customization', <IUserConfig>{
        module_type: moduleType,
        module_name: moduleName,
        module_config: moduleConfig,
      })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  /* eslint-disable */
  private catchHttpErrorAndPickErrorList =
    () =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) =>
          observableThrowError(get(error, 'errors')),
        ),
      );
}
